import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { debounceTime, firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CareerRoleFullPictureResponse,
  CareerRolePickerItemResponse,
  SkillWithResourcesResponse
} from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';
import {
  CopyProfileSkillsResult,
  getProfileSkills,
  isSkillCopyConflict,
  mapSkillToAddRequest
} from './profile-skill-copy.util';

export interface CopyProfileSkillsDialogData {
  roleSlug: string;
  level: string;
  specializationSlug?: string;
}

@Component({
  selector: 'app-copy-profile-skills-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    MatSelectModule
  ],
  template: `
    <h2 mat-dialog-title>
      Copy skills to {{ data.level }}{{ data.specializationSlug ? ' / ' + data.specializationSlug : '' }}
    </h2>
    <mat-progress-bar *ngIf="loading || saving" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content class="content">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Source role</mat-label>
          <input
            matInput
            [formControl]="sourceRoleSearchControl"
            [matAutocomplete]="sourceRoleAuto"
            placeholder="Search active roles" />
          <mat-autocomplete
            #sourceRoleAuto="matAutocomplete"
            [displayWith]="displaySourceRole"
            (optionSelected)="onSourceRoleSelected($event.option.value)">
            <mat-option *ngFor="let role of sourceRoleResults" [value]="role">
              {{ role.roleName }} ({{ role.roleSlug }})
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Source level</mat-label>
          <mat-select formControlName="sourceLevel" (selectionChange)="onSourceProfileContextChange()">
            <mat-option *ngFor="let level of sourceLevels" [value]="level">{{ level }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width" *ngIf="data.specializationSlug">
          <mat-label>Source specialization</mat-label>
          <mat-select formControlName="sourceSpecializationSlug" (selectionChange)="onSourceProfileContextChange()">
            <mat-option *ngFor="let spec of sourceSpecializations" [value]="spec.slug">
              {{ spec.name }} ({{ spec.slug }})
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Skills to copy</mat-label>
          <mat-select formControlName="skillIds" multiple>
            <mat-option *ngFor="let skill of sourceProfileSkills" [value]="skill.skill.id">
              {{ skill.skill.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <p class="hint" *ngIf="!sourceProfileSkills.length && sourcePicture">No skills on the selected source profile.</p>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="!canSubmit || saving">Copy</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .content { min-width: 480px; display: flex; flex-direction: column; gap: 0.5rem; }
      .full-width { width: 100%; }
      .hint { margin: 0; color: #64748b; font-size: 0.85rem; }
      .error { color: #b00020; }
    `
  ]
})
export class CopyProfileSkillsDialogComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(MatDialogRef<CopyProfileSkillsDialogComponent, CopyProfileSkillsResult | undefined>);
  readonly data = inject<CopyProfileSkillsDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  loading = false;
  saving = false;
  error = '';
  sourceRoleResults: CareerRolePickerItemResponse[] = [];
  sourceLevels: string[] = [];
  sourceSpecializations: { slug: string; name: string }[] = [];
  sourceProfileSkills: SkillWithResourcesResponse[] = [];
  sourcePicture: CareerRoleFullPictureResponse | null = null;

  readonly sourceRoleSearchControl = this.fb.nonNullable.control<string | CareerRolePickerItemResponse>('');
  readonly form = this.fb.nonNullable.group({
    sourceLevel: [''],
    sourceSpecializationSlug: [''],
    skillIds: this.fb.nonNullable.control<string[]>([])
  });

  get canSubmit(): boolean {
    return (this.form.value.skillIds?.length ?? 0) > 0 && !this.saving;
  }

  constructor() {
    this.sourceRoleSearchControl.valueChanges.pipe(debounceTime(250)).subscribe((value) => {
      if (typeof value === 'string') {
        void this.searchSourceRoles(value);
      }
    });
    void this.searchSourceRoles('');
  }

  displaySourceRole(role: CareerRolePickerItemResponse | string | null): string {
    if (!role || typeof role === 'string') {
      return typeof role === 'string' ? role : '';
    }
    return `${role.roleName} (${role.roleSlug})`;
  }

  async onSourceRoleSelected(role: CareerRolePickerItemResponse): Promise<void> {
    this.loading = true;
    this.error = '';
    this.sourcePicture = null;
    this.sourceProfileSkills = [];
    this.form.patchValue({ sourceLevel: '', sourceSpecializationSlug: '', skillIds: [] });
    try {
      this.sourcePicture = await firstValueFrom(this.api.getFullPicture(role.roleSlug));
      this.sourceLevels = this.sourcePicture.seniorityProfiles.map((p) => p.level);
      this.sourceSpecializations = this.sourcePicture.specializations.map((s) => ({
        slug: s.slug,
        name: s.name
      }));
      if (this.sourceLevels.length === 1) {
        this.form.patchValue({ sourceLevel: this.sourceLevels[0] });
      }
      if (this.data.specializationSlug && this.sourceSpecializations.length === 1) {
        this.form.patchValue({ sourceSpecializationSlug: this.sourceSpecializations[0].slug });
      }
      this.onSourceProfileContextChange();
    } catch (error) {
      this.error = this.actions.errorMessage(error);
    } finally {
      this.loading = false;
    }
  }

  onSourceProfileContextChange(): void {
    if (!this.sourcePicture) {
      this.sourceProfileSkills = [];
      return;
    }
    const level = this.form.value.sourceLevel ?? '';
    const specSlug = this.data.specializationSlug ? this.form.value.sourceSpecializationSlug ?? '' : undefined;
    if (!level || (this.data.specializationSlug && !specSlug)) {
      this.sourceProfileSkills = [];
      this.form.patchValue({ skillIds: [] });
      return;
    }
    this.sourceProfileSkills = getProfileSkills(this.sourcePicture, level, specSlug || undefined);
    this.form.patchValue({ skillIds: [] });
  }

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (!this.canSubmit || this.saving) {
      return;
    }
    this.saving = true;
    this.error = '';
    const result: CopyProfileSkillsResult = { copied: 0, skipped: 0, failed: 0, errors: [] };
    const selectedIds = this.form.value.skillIds ?? [];

    for (const skillId of selectedIds) {
      const skill = this.sourceProfileSkills.find((s) => s.skill.id === skillId);
      if (!skill) {
        continue;
      }
      const request = mapSkillToAddRequest(skill);
      if (!request) {
        result.skipped++;
        result.errors.push(`${skill.skill.name}: no topics to copy`);
        continue;
      }
      try {
        if (this.data.specializationSlug) {
          await firstValueFrom(
            this.api.addCareerRoleFocusSkill(
              this.data.roleSlug,
              this.data.specializationSlug,
              this.data.level,
              request
            )
          );
        } else {
          await firstValueFrom(this.api.addCareerRoleCoreSkill(this.data.roleSlug, this.data.level, request));
        }
        result.copied++;
      } catch (error) {
        if (isSkillCopyConflict(error)) {
          result.skipped++;
        } else {
          result.failed++;
          result.errors.push(`${skill.skill.name}: ${this.actions.errorMessage(error)}`);
        }
      }
    }

    this.saving = false;
    if (result.copied === 0 && result.failed > 0 && result.skipped === 0) {
      this.error = result.errors[0] ?? 'Copy failed';
      return;
    }
    this.dialogRef.close(result);
  }

  private async searchSourceRoles(text: string): Promise<void> {
    this.loading = true;
    try {
      const page = await firstValueFrom(this.api.listCareerRolesPicker(1, 30, text, true));
      this.sourceRoleResults = page.data ?? [];
    } catch {
      this.sourceRoleResults = [];
    } finally {
      this.loading = false;
    }
  }
}